import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, CalendarX, LayoutGrid, List } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import AppointmentItem from "@/components/AppointmentItem";
import StatusBadge from "@/components/StatusBadge";
import BookingModal from "@/components/BookingModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const todayStr = () => new Date().toISOString().slice(0, 10);
const shiftDate = (d, n) => { const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt.toISOString().slice(0, 10); };
const pretty = (d) => new Date(d).toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" });

const ADMIN_ACTIONS = [
  { label: "Tamamlandı", status: "completed", testId: "appt-complete" },
  { label: "Gelmedi", status: "no_show", testId: "appt-noshow" },
  { label: "İptal", status: "cancelled", testId: "appt-cancel" },
];

const TIMES = ["08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00"];

// Shared calendar day view for admin & trainer
export default function Calendar({ role = "admin" }) {
  const [date, setDate] = useState(todayStr());
  const [items, setItems] = useState(null);
  const [waitCounts, setWaitCounts] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [view, setView] = useState("grid");
  const [prefill, setPrefill] = useState(null);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    setItems(null);
    api.get(`/appointments?date=${date}`).then((r) => setItems(r.data)).catch(() => setItems([]));
    if (role === "admin") api.get("/waitlist/counts").then((r) => setWaitCounts(r.data)).catch(() => setWaitCounts([]));
  }, [date, role]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (role === "admin") api.get("/trainers").then((r) => setTrainers(r.data)).catch(() => setTrainers([]));
  }, [role]);

  const waitersFor = (a) => {
    const m = waitCounts.find((w) => w.service_id === a.service_id && w.date === a.date && w.start_time === a.start_time);
    return m ? m.count : 0;
  };

  const onStatus = async (id, status) => {
    try { await api.put(`/appointments/${id}/status`, { status }); toast.success("Randevu güncellendi"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid={`${role}-calendar-page`}>
      <PageHeader title="Takvim & Randevular" subtitle="Günlük ders programı"
        action={role === "admin" ? <Button onClick={() => setOpen(true)} data-testid="new-appointment-button" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold rounded-xl"><Plus size={18} className="mr-1" /> Yeni Randevu</Button> : null} />

      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => setDate(shiftDate(date, -1))} data-testid="cal-prev" className="p-2.5 rounded-xl bg-[#13131C] border border-[#222230] text-white hover:border-[#FF5500]/40"><ChevronLeft size={18} /></button>
        <div className="flex-1 text-center">
          <p className="font-display font-bold text-white capitalize">{pretty(date)}</p>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="cal-date" className="mt-1 h-8 w-40 mx-auto bg-[#0E0E14] border-[#262636] text-white text-xs [color-scheme:dark]" />
        </div>
        <button onClick={() => setDate(shiftDate(date, 1))} data-testid="cal-next" className="p-2.5 rounded-xl bg-[#13131C] border border-[#222230] text-white hover:border-[#FF5500]/40"><ChevronRight size={18} /></button>
      </div>

      {role === "admin" && (
        <div className="flex gap-1.5 mb-4" data-testid="calendar-view-toggle">
          <button onClick={() => setView("grid")} data-testid="view-grid-button"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${view === "grid" ? "bg-[#FF5500] text-white" : "bg-[#13131C] text-[#A0A0B2] border border-[#222230] hover:text-white"}`}>
            <LayoutGrid size={14} /> Antrenör Görünümü
          </button>
          <button onClick={() => setView("list")} data-testid="view-list-button"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${view === "list" ? "bg-[#FF5500] text-white" : "bg-[#13131C] text-[#A0A0B2] border border-[#222230] hover:text-white"}`}>
            <List size={14} /> Liste
          </button>
        </div>
      )}

      {items === null ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 rounded-2xl bg-[#13131C]" />)}</div>
      ) : view === "grid" && role === "admin" ? (
        <div className="overflow-x-auto rounded-2xl border border-[#222230]" data-testid="calendar-grid">
          <div className="min-w-max">
            <div className="grid" style={{ gridTemplateColumns: `56px repeat(${Math.max(trainers.length, 1)}, minmax(150px, 1fr))` }}>
              <div className="bg-[#181824] border-b border-[#1A1A24]" />
              {trainers.map((t) => (
                <div key={t.id} data-testid={`grid-trainer-${t.id}`} className="bg-[#181824] border-b border-l border-[#1A1A24] px-2 py-2.5 text-center">
                  <p className="text-xs font-bold text-white truncate">{t.name}</p>
                </div>
              ))}
              {TIMES.map((time) => (
                <React.Fragment key={time}>
                  <div className="px-1 py-1 text-[11px] font-mono text-[#68687B] border-b border-[#1A1A24] flex items-start justify-center">{time}</div>
                  {trainers.map((t) => {
                    const cellAppts = (items || []).filter((a) => a.trainer_id === t.id && a.start_time === time);
                    return (
                      <div key={t.id + time}
                        onClick={() => { setPrefill({ trainer_id: t.id, start_time: time }); setOpen(true); }}
                        data-testid={`grid-cell-${t.id}-${time.replace(":", "")}`}
                        className="min-h-[54px] p-1 border-b border-l border-[#1A1A24] hover:bg-[#1B1B27] cursor-pointer transition-colors">
                        {cellAppts.map((a) => (
                          <div key={a.id} data-testid={`grid-appt-${a.id}`} onClick={(e) => { e.stopPropagation(); setSelected(a); }}
                            className={`text-[11px] rounded-lg px-1.5 py-1 mb-1 border leading-tight cursor-pointer hover:brightness-125 transition ${a.status === "scheduled" ? "bg-[#FF5500]/10 border-[#FF5500]/30 text-white" : a.status === "completed" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-[#68687B]/10 border-[#68687B]/20 text-[#68687B] line-through"}`}>
                            <span className="font-semibold">{a.member_name}</span> · {a.service_name}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center py-20 text-[#68687B]"><CalendarX size={40} className="mb-3 opacity-50" />Bu gün için randevu yok.</div>
      ) : (
        <div className="space-y-2">
          {items.map((a) => <AppointmentItem key={a.id} appt={a} actions={ADMIN_ACTIONS} onStatus={onStatus} waiters={waitersFor(a)} />)}
        </div>
      )}

      <BookingModal open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPrefill(null); }} mode="admin" defaultDate={date}
        defaultTrainerId={prefill?.trainer_id} defaultTime={prefill?.start_time} onBooked={load} />

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-sm" data-testid="grid-appt-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">{selected?.member_name}</DialogTitle>
            <DialogDescription className="text-[#68687B]">
              {selected?.date} {selected?.start_time} · {selected?.service_name} · {selected?.trainer_name}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 py-1">
            <span className="text-xs text-[#A0A0B2]">Durum:</span>
            {selected && <StatusBadge status={selected.status} />}
          </div>
          {selected?.status === "scheduled" && (
            <DialogFooter className="gap-2 sm:gap-2">
              {ADMIN_ACTIONS.map((act) => (
                <Button key={act.status} size="sm" variant="secondary" data-testid={`grid-action-${act.status}`}
                  onClick={async () => { await onStatus(selected.id, act.status); setSelected(null); }}
                  className="h-8 bg-[#1C1C28] hover:bg-[#252536] text-white border border-[#2D2D3F] text-xs">
                  {act.label}
                </Button>
              ))}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
